import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatSort, MatTableDataSource, MatPaginator } from '@angular/material';
import { LessonStatus, STATUS_CLASSES, STATUS_LABELS, StudentClass, Profile, ShortDataLesson } from '@df/interfaces';

import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

import { PeopleService } from '@app/core/services';
import { AccountService } from '@app/teacher/services/account.service';
import { LessonsListService } from '@app/teacher/components/lessons-list/lessons-list.service';

import { CopyFormComponent } from '@app/teacher/components/prepare/copy-form/copy-form.component';
import { EditLessonModalComponent } from '@app/teacher/components/prepare/edit-lesson-modal/edit-lesson-modal.component';
import { DeleteFormComponent } from '@app/teacher/components/prepare/delete-form/delete-form.component';

import { GROUPS, DEFAULT_ACCESS_RIGHTS } from '@app/app.constants';
import { LessonPagination, ISubject, School } from '@df/interfaces/lesson';

import { copyIcon } from '@app/app.constants';
import { removeIcon } from '@app/app.constants';

@Component({
    selector: 'app-lessons-list',
    templateUrl: './lessons-list.component.html',
    styleUrls: ['./lessons-list.component.scss']
})
export class LessonsListComponent implements OnInit, OnDestroy {
    @Input() showHeader: boolean = true;
    @Input() lessonStatus: LessonStatus = null;
    @Input() studentClassId: number = null;
    @Input() subjectId: number = null;
    @Input() onlyStatuses: Array<number> = [
        LessonStatus.LESSON_CANCELED,
        LessonStatus.LESSON_COMPLETED,
        LessonStatus.LESSON_COMPLETED_NOT_FINAL,
        LessonStatus.LESSON_CREATED,
        LessonStatus.LESSON_IN_WORK,
        LessonStatus.LESSON_PRE_CREATED
    ];

    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

    private $destroy = new Subject<void>();
    private teacherClasses: StudentClass[] = [];

    dataSource: MatTableDataSource<LessonPagination>;
    lessons: LessonPagination[] = [];
    sourceLessons: LessonPagination[] = [];

    is_student: boolean = false;
    student: any;

    copyIcon = copyIcon;
    removeIcon = removeIcon;

    displayedColumns = ['lesson_number', 'date', 'student_class_title', 'title', 'status', 'actions'];
    STATUS_LABELS = STATUS_LABELS;
    profile: Profile;
    group_rights = DEFAULT_ACCESS_RIGHTS;

    subjects: ISubject[] = [];
    currentSubject: ISubject;

    showSchoolFilter: boolean = false;
    schools: School[] = [];
    currentSchool: School;

    filterValue = '';

    constructor(
        private lessonsListService: LessonsListService,
        private peopleService: PeopleService,
        private accountService: AccountService,
        public dialog: MatDialog,
        private router: Router
    ) {}

    private checkSearchValue(): void {
        const thisSeacrhValue = localStorage.getItem('lessons_search_value');
        if (thisSeacrhValue) {
            this.dataSource.filter = thisSeacrhValue;
            this.filterValue = thisSeacrhValue;
        }
    }

    private getSubjects() {
        // Создаем Set для уникальных значений
        const uniqueSubjects = new Set();

        // Используем map для извлечения уникальных объектов Subject
        const uniqueSubjectObjects = this.lessons
            .map(it => {
                // Проверяем, содержится ли объект Subject в Set'е уникальных значений
                // Если это новый объект Subject, добавляем его в Set и возвращаем его
                if (!uniqueSubjects.has(it.subject.id)) {
                    uniqueSubjects.add(it.subject.id);
                    return it.subject;
                }
                // Если объект Subject уже содержится в Set'е, возвращаем null
                return null;
            })
            .filter(it => it !== null);
        this.subjects = uniqueSubjectObjects;
    }

    private getSchools() {
        // Создаем Set для уникальных значений
        const uniqueSchools = new Set();

        // Используем map для извлечения уникальных объектов School
        const uniqueSchoolObjects = this.lessons
            .map(it => {
                // Проверяем, содержится ли объект School в Set'е уникальных значений
                // Если это новый объект School, добавляем его в Set и возвращаем его
                if (!uniqueSchools.has(it.student_class.school.id)) {
                    uniqueSchools.add(it.student_class.school.id);
                    return it.student_class.school;
                }
                // Если объект School уже содержится в Set'е, возвращаем null
                return null;
            })
            .filter(it => it !== null);
        this.schools = uniqueSchoolObjects;
    }

    private setTableDate() {
        this.dataSource = new MatTableDataSource<LessonPagination>(this.lessons);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        this.checkSearchValue();
    }

    private getShortLessonsData() {
        this.lessonsListService.getShortLessonsData().subscribe(resp => {
            this.lessons = resp.filter(item => {
                return this.onlyStatuses.includes(item.status);
            });
            this.sourceLessons = this.lessons;

            this.getSubjects();
            this.setTableDate();
        });
    }

    private setWithRightsData(): void {
        if (this.profile.group.name === GROUPS.STUDENT) {
            this.is_student = true;
            this.displayedColumns = ['lesson_number', 'date', 'title'];
            this.student = this.accountService.currentGroupInstance;
        }

        if (
            this.profile.group.name === GROUPS.NAVIGATOR ||
            this.profile.group.name === GROUPS.NAVIGATOR_JUNIOR ||
            this.profile.group.name === GROUPS.SUPER_NAVIGATOR ||
            this.profile.group.name === GROUPS.METODIST
        ) {
            this.showSchoolFilter = true;
            this.getSchools();
        }
    }

    ngOnInit() {
        this.getShortLessonsData();
        this.getTeacherClasses();

        this.accountService.getProfileData().subscribe(response => {
            if (response.data && response.data.user.username) {
                this.profile = response.data;
                this.checkAccess();
                this.setWithRightsData();
            }
        });
    }

    ngOnDestroy() {
        this.$destroy.next();
    }

    getStatusClass(status: number): number {
        return STATUS_CLASSES[status];
    }

    editLesson(lesson: LessonPagination) {
        const status = this.lessonStatus || lesson.status;
        if (this.is_student) {
            if (status === LessonStatus.LESSON_CREATED) {
                const url = `/student/pupil/${this.profile.user.first_name}/lesson_not_started`;
                this.router.navigate([url]);
                return;
            }

            if (status === LessonStatus.LESSON_IN_WORK) {
                this.router.navigate([`/student/pupil/${this.student.id}`]);
                return;
            }

            if (status === LessonStatus.LESSON_COMPLETED || status === LessonStatus.LESSON_COMPLETED_NOT_FINAL) {
                this.router.navigate([`/profile/analytics/${lesson.id}`]);
                return;
            }
        }

        if (lesson.is_author && this.group_rights.edit_other_lesson.is_access) {
            const routerLink = this.formRouterLink(lesson);
            this.router.navigate(routerLink);
            return;
        }

        if (!lesson.is_author && this.group_rights.view_other_lesson.is_access) {
            const routerLink = this.formRouterLink(lesson);
            this.router.navigate(routerLink);
            return;
        }
    }

    updateStatus(lesson: LessonPagination) {
        if (lesson.status !== LessonStatus.LESSON_COMPLETED_NOT_FINAL) {
            return;
        }

        if (lesson.is_author || this.group_rights.change_status_other_lesson.is_access) {
            const dialogRef = this.dialog.open(EditLessonModalComponent, {
                width: '630px',
                height: '355px',
                data: {
                    lesson: lesson
                }
            });

            dialogRef.afterClosed().subscribe(result => {
                if (!result) {
                    return;
                }
                this.getShortLessonsData();
            });
        }
    }

    openCopyDialog(sourceLesson: LessonPagination): void {
        const dialogRef = this.dialog.open(CopyFormComponent, {
            width: '630px',
            height: '610px',
            data: {
                lesson: sourceLesson,
                classTitle: sourceLesson.student_class.title,
                classID: sourceLesson.student_class.id,
                teacherClasses: this.teacherClasses
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.getShortLessonsData();
        });
    }

    // todo не перезагружать страницу?
    openRemoveDialog(sourceLesson: LessonPagination): void {
        const dialogRef = this.dialog.open(DeleteFormComponent, {
            width: '630px',
            height: '320px',
            data: {
                lesson: sourceLesson,
                classTitle: sourceLesson.student_class.title
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.getShortLessonsData();
        });
    }

    private formRouterLink(lesson: LessonPagination): string[] {
        const status = this.lessonStatus || lesson.status;
        let routerLink = [];

        if (status === LessonStatus.LESSON_CREATED || status === LessonStatus.LESSON_IN_WORK) {
            routerLink = ['/teacher/teach/', lesson.student_class.id, lesson.subject.id, 'progress', lesson.id];
        }

        if (status === LessonStatus.LESSON_COMPLETED || status === LessonStatus.LESSON_COMPLETED_NOT_FINAL) {
            routerLink = [
                '/teacher/analysis',
                lesson.student_class.id,
                lesson.subject.id,
                'analytics',
                lesson.id,
                'commontable'
            ];
        }

        if (this.lessonStatus === null) {
            routerLink = ['/teacher/prepare', lesson.id, 'edit'];
        }
        return routerLink;
    }

    private getTeacherClasses() {
        this.peopleService
            .getClasses()
            .pipe(takeUntil(this.$destroy))
            .subscribe(response => {
                this.teacherClasses = response.results;
            });
    }

    private checkAccess(): void {
        const user_rights = this.profile.group.rights;

        user_rights.forEach(item => {
            switch (+item.index_title) {
                // доступ к просмотру чужих уроков
                case this.group_rights.view_other_lesson.index_title:
                    this.group_rights.view_other_lesson.is_access = true;
                    break;
                // доступ к изменению статуса урока
                case this.group_rights.change_status_other_lesson.index_title:
                    this.group_rights.change_status_other_lesson.is_access = true;
                    break;
                // доступ к копированию чужих уроков
                case this.group_rights.copy_other_lesson.index_title:
                    this.group_rights.copy_other_lesson.is_access = true;
                    break;
                // доступ к созданию урока
                case this.group_rights.create_lesson.index_title:
                    this.group_rights.create_lesson.is_access = true;
                    break;
                // доступ к удалению чужого урока
                case this.group_rights.delete_other_lesson.index_title:
                    this.group_rights.delete_other_lesson.is_access = true;
                    break;
                // доступ к редактированию чужого урока
                case this.group_rights.edit_other_lesson.index_title:
                    this.group_rights.edit_other_lesson.is_access = true;
                    break;
                // доступ к редактированию шаблонов
                case this.group_rights.edit_templates.index_title:
                    this.group_rights.edit_templates.is_access = true;
                    break;
                // доступ к копированию шаблонов
                case this.group_rights.copy_templates.index_title:
                    this.group_rights.copy_templates.is_access = true;
                    break;
                // доступ к удалению шаблонов
                case this.group_rights.delete_templates.index_title:
                    this.group_rights.delete_templates.is_access = true;
                    break;
                default:
                    break;
            }
        });
    }

    applySearch(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
        this.filterValue = filterValue.trim().toLowerCase();
        localStorage.setItem('lessons_search_value', filterValue.trim().toLowerCase());
    }

    public copyLessonAccess(lesson: ShortDataLesson) {
        // Если является автором урока, то разрешаем
        if (lesson.is_author) {
            return true;
        }
        // если есть право копирования чужих занятий, то разрешаем,
        // но с условиями
        if (this.group_rights.copy_other_lesson.is_access) {
            let access = true;

            // для шаблонов есть отдельные права доступа
            if (lesson.student_class_title.includes('Шаблон')) {
                access = this.group_rights.copy_templates.is_access;
            }
            return access;
        }
        return false;
    }

    public deleteLessonAccess(lesson: ShortDataLesson) {
        // запрещаем если урок имеет статус Завершенно
        if (lesson.status === LessonStatus.LESSON_COMPLETED) {
            return false;
        }
        // Если является автором урока, то разрешаем
        if (lesson.is_author) {
            return true;
        }
        // если есть право удалять чужие занятия, то разрешаем,
        // но с условиями
        if (this.group_rights.delete_other_lesson.is_access) {
            let access = true;

            // для шаблонов есть отдельные права доступа
            if (lesson.student_class_title.includes('Шаблон')) {
                access = this.group_rights.delete_templates.is_access;
            }
            return access;
        }
        return false;
    }

    changeFilter(): void {
        this.lessons = this.sourceLessons
            .filter(i => (this.currentSubject && this.currentSubject.id ? i.subject.id === this.currentSubject.id : i))
            .filter(i =>
                this.currentSchool && this.currentSchool.id ? i.student_class.school.id === this.currentSchool.id : i
            );

        this.setTableDate();
    }

    changeSchool(school: School) {
        this.currentSchool = school;
        this.changeFilter();
    }

    changeSubject(subject: ISubject): void {
        this.currentSubject = subject;
        this.changeFilter();
    }
}
